<template>
  <div>
    <h4 class="text-center mb-3">
      {{ editMode ? 'Modifier une catégorie' : 'Ajouter une catégorie' }}
    </h4>
    <form @submit.prevent="submitForm" data-cy="form-categorie">
      <div class="mb-3">
        <input-component
          id="input-categorie-nom"
          label="Nom"
          v-model="formData.nom"
          type="text"
          placeholder="Saisir le nom"
          :class="{ 'is-invalid': errors.nom }"
          @input="clearError('nom')"
          @blur="validateNom"
          required
        />
        <div v-if="errors.nom" class="invalid-feedback d-block">
          {{ errors.nom }}
        </div>
      </div>

      <div class="mb-3">
        <input-component
          id="input-categorie-montant"
          label="Montant maximum"
          v-model="formData.montantMax"
          type="text"
          placeholder="Saisir un montant maximum (ex: 100,50)"
          :class="{ 'is-invalid': errors.montantMax }"
          @input="clearError('montantMax')"
          @blur="validateMontantMax"
          required
        />
        <div v-if="errors.montantMax" class="invalid-feedback d-block">
          {{ errors.montantMax }}
        </div>
      </div>

      <div class="mb-3">
        <colorpicker-component
          id="input-categorie-couleur"
          label="Couleur"
          placeholder="Saisir ou sélectionner une couleur (ex: #FF5733)"
          v-model:color="formData.couleur"
          :class="{ 'is-invalid': errors.couleur }"
          @input="clearError('couleur')"
          @change="validateCouleur"
          required
        />
        <div v-if="errors.couleur" class="invalid-feedback d-block">
          {{ errors.couleur }}
        </div>
      </div>

      <div class="mb-3">
        <icon-selection-component
          v-model="formData.icone"
          label="Icône"
          placeholder="Sélectionnez votre icône"
        />
        <div v-if="errors.icone" class="invalid-feedback d-block">
          {{ errors.icone }}
        </div>
      </div>

      <div class="form-actions">
        <button
          type="button"
          class="glass-btn-danger-outlined rounded-5 py-2 px-3"
          data-cy="form-categorie-cancel"
          @click="$emit('close')"
        >
          Annuler
        </button>
        <button
          type="submit"
          class="glass-btn-success rounded-5 py-2 px-3"
          data-cy="form-categorie-submit"
          :disabled="isSubmitting"
        >
          {{ editMode ? 'Modifier' : 'Ajouter' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
// Component
import InputComponent from '@/components/InputComponent.vue'
import ColorpickerComponent from '@/components/ColorpickerComponent.vue'
import IconSelectionComponent from '@/components/IconSelectionComponent.vue'

// Store
import useCategorieStore from '@/stores/categorie'

export default {
  name: 'CategorieFormBlock',

  components: {
    InputComponent,
    ColorpickerComponent,
    IconSelectionComponent
  },

  props: {
    editMode: {
      type: Boolean,
      default: false
    },
    itemToEdit: {
      type: Object,
      default: () => ({})
    }
  },

  setup() {
    const { addCategorie, updateCategorie } = useCategorieStore()
    return {
      addCategorie,
      updateCategorie
    }
  },

  emits: ['close'],

  data() {
    return {
      formData: {
        nom: null,
        montantMax: null,
        couleur: '#753939',
        icone: null
      },
      errors: {
        nom: null,
        montantMax: null,
        couleur: null,
        icone: null
      },
      isSubmitting: false
    }
  },

  mounted() {
    if (this.editMode && this.itemToEdit) {
      this.formData = {
        nom: this.itemToEdit.libelle,
        montantMax: this.itemToEdit.limite.toString(),
        couleur: `#${this.itemToEdit.couleur}`,
        icone: this.itemToEdit.icone
      }
    }
  },

  methods: {
    validateNom() {
      const nom = this.formData.nom
      if (!nom || nom.trim() === '') {
        this.errors.nom = 'Le nom est obligatoire'
        return false
      }

      // Regex pour valider le nom : lettres, nombres, espaces, tirets, caractères UTF-8 uniquement
      const validNameRegex = /^[\p{L}\p{M}\p{N}\s-]+$/u
      if (!validNameRegex.test(nom)) {
        this.errors.nom = 'Le nom ne doit contenir que des lettres, chiffres, espaces et tirets'
        return false
      }

      // Le nom doit avoir au maximum 100 caractères
      if (nom.length > 100) {
        this.errors.nom = 'Le nom ne doit pas dépasser 100 caractères'
        return false
      }

      this.errors.nom = null
      return true
    },

    validateMontantMax() {
      const montantMax = this.formData.montantMax
      if (!montantMax) {
        this.errors.montantMax = 'Le montant maximum est obligatoire'
        return false
      }

      // Convertir en string pour validation
      const montantStr = montantMax.toString()

      // Regex pour valider un montant en euros avec max 2 décimales
      const validAmountRegex = /^\d+([.,]\d{1,2})?$/
      if (!validAmountRegex.test(montantStr)) {
        this.errors.montantMax =
          'Le montant doit être un nombre avec maximum 2 décimales (ex: 100,50)'
        return false
      }

      const numericValue = parseFloat(montantStr.replace(',', '.'))
      if (numericValue <= 0) {
        this.errors.montantMax = 'Le montant maximum doit être supérieur à 0'
        return false
      }

      if (numericValue > 10000000) {
        this.errors.montantMax = 'Le montant maximum ne doit pas dépasser 10 000 000'
        return false
      }

      this.errors.montantMax = null
      return true
    },

    validateIcone() {
      const icone = this.formData.icone
      if (!icone || icone.trim() === '') {
        this.errors.icone = "L'icône est obligatoire"
        return false
      }

      this.errors.icone = null
      return true
    },

    validateCouleur() {
      const couleur = this.formData.couleur
      if (!couleur || couleur.trim() === '') {
        this.errors.couleur = 'La couleur est obligatoire'
        return false
      }

      // Regex pour valider une couleur hexadécimale (avec ou sans #)
      const validHexColorRegex = /^#?[0-9A-Fa-f]{6}$/
      if (!validHexColorRegex.test(couleur)) {
        this.errors.couleur = 'La couleur doit être en format hexadécimal (ex: #FF5733)'
        return false
      }

      this.errors.couleur = null
      return true
    },

    validateForm() {
      const isNomValid = this.validateNom()
      const isMontantMaxValid = this.validateMontantMax()
      const isCouleurValid = this.validateCouleur()
      const isIconeValid = this.validateIcone()

      return isNomValid && isMontantMaxValid && isCouleurValid && isIconeValid
    },

    clearError(field) {
      this.errors[field] = null
    },

    async submitForm() {
      if (this.isSubmitting) return

      // Valider le formulaire avant soumission
      if (!this.validateForm()) {
        this.$toast.error('Veuillez corriger les erreurs dans le formulaire')
        return
      }

      this.isSubmitting = true

      try {
        const formattedFormInfo = {
          libelle: this.formData.nom.trim(),
          limite: parseFloat(this.formData.montantMax.toString().replace(',', '.')),
          couleur: this.formData.couleur.startsWith('#')
            ? this.formData.couleur.replace('#', '')
            : this.formData.couleur,
          icone: this.formData.icone
        }

        if (this.editMode) {
          formattedFormInfo.id = this.itemToEdit.id
          await this.$toast.promise(this.updateCategorie(formattedFormInfo), {
            pending: 'Mise à jour en cours...',
            success: {
              render: 'Catégorie mise à jour !',
              autoClose: 1500
            },
            error: {
              render: 'Erreur lors de la mise à jour de la catégorie.',
              autoClose: 1500
            }
          })
        } else {
          await this.$toast.promise(this.addCategorie(formattedFormInfo), {
            pending: 'Ajout en cours...',
            success: {
              render: 'Catégorie ajoutée !',
              autoClose: 1500
            },
            error: {
              render: "Erreur lors de l'ajout de la catégorie.",
              autoClose: 1500
            }
          })
        }

        this.$emit('close')
      } catch (error) {
        console.error("Erreur lors de l'ajout de la catégorie:", error)
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped></style>
